/**
 * Translate raw mutation errors from the facilities table into user-facing
 * messages. The DB enforces a UNIQUE constraint on `sage_location_id`; when
 * the constraint trips, callers receive either:
 *  - a thrown `Error` (e.g. from React Query / `mutateAsync` rejection), or
 *  - a Supabase `PostgrestError` (plain object with `message`/`code`
 *    fields) returned via `{ data, error }` destructuring.
 * Both shapes are checked here; UNIQUE violations are mapped to a
 * copy-friendly hint instead of leaking the raw SQL error text.
 *
 * @param error - The error value (`unknown`, since neither RHF / React Query
 *                nor Supabase narrows the rejection type at the call site).
 * @param fallback - Generic message used when the error is missing a
 *                   readable payload.
 * @returns The toast-ready message to display.
 */
export function formatFacilityMutationError(
  error: unknown,
  fallback: string,
): string {
  const message = extractErrorMessage(error)
  const code = extractErrorCode(error)
  if (message === null && code === null) {
    return fallback
  }

  const lowerMsg = (message ?? '').toLowerCase()
  // Postgres surfaces UNIQUE violations either via SQLSTATE `23505` or a
  // human-readable message naming both the violation kind and the column.
  const isUniqueViolation =
    code === '23505' ||
    lowerMsg.includes('23505') ||
    lowerMsg.includes('unique') ||
    lowerMsg.includes('duplicate')
  if (isUniqueViolation && lowerMsg.includes('sage_location_id')) {
    return 'Sage Facility ID already exists. Please use a different ID.'
  }

  return message ?? fallback
}

/**
 * Pull a string `message` field off `Error` instances or plain objects
 * (Supabase `PostgrestError`). Returns `null` when no readable message can
 * be extracted.
 */
function extractErrorMessage(error: unknown): string | null {
  if (error instanceof Error) return error.message
  if (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  ) {
    return (error as { message: string }).message
  }
  return null
}

/**
 * Pull a string `code` field off Supabase-shaped errors. Plain `Error`
 * instances carry no SQLSTATE, so this returns `null` for them.
 */
function extractErrorCode(error: unknown): string | null {
  if (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    typeof (error as { code: unknown }).code === 'string'
  ) {
    return (error as { code: string }).code
  }
  return null
}
